import { Component, computed, inject, signal, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { map } from 'rxjs';
import { AssignmentWithRelations } from '../../../api/app-types/assignment';
import { DB_PHASES } from '../../../api/app-types/phase.app';
import { ValidationPhaseResponse } from '../../../api/app-types/validation.app';
import { ASSIGNMENT_API, provideAssignmentApi } from '../../../api/services/assignment.service';
import { AssignmentChecks } from '../../../core/components/assignment-checks/assignment-checks';
import { AssignmentLinks } from '../../../core/components/assignment-links/assignment-links';
import { FdsAssignmentDetails } from '../../client/client-assignments/fds-assignment-details/fds-assignment-details';
import { LvrDefaultActions } from '../../../core/components/default-actions/default.actions';
import { STATE } from '../../../core/services/state.service';
import { LvrPaddingDirective } from '../../../shared/padding/padding.directive';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { RouteInjection } from '../../../shared/routing/route-injection.class';
import { FormPersistenceService } from '../../../shared/storage/form-persistence.service';
import { LOGGER } from '../../../shared/logging/log.service';
import { TranslocoService } from '@jsverse/transloco';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { PaginatedResponse } from '../../../shared/pagination/paginated-response';
import { REPORT_TYPE, ReportDocument } from '../../../api/app-types/report-document.app';
import { createErrorNotification, createSuccessNotification } from '../../../shared/notification/notification.factory';

@Component({
	selector: 'lvr-fds-assignment-move',
	templateUrl: './fds-assignment-move.html',
	imports: [
		TranslocoPipe,
		LvrPaddingDirective,
		FdsAssignmentDetails,
		AssignmentLinks,
		AssignmentChecks,
		LvrDefaultActions,
	],
	styleUrl: './fds-assignment-move.scss',
	providers: [FormPersistenceService, provideAssignmentApi],
})
export class FdsAssignmentMove extends RouteInjection {
	private readonly transloco = inject(TranslocoService);
	private readonly activeRoute = inject(ActivatedRoute);
	private readonly api = inject(ASSIGNMENT_API);
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly state = inject(STATE);
	private readonly draftService = inject(FormPersistenceService);
	private readonly session = inject(SessionManagerService);
	public readonly currentUrl = this.router.url;
	private readonly logger = inject(LOGGER);
	private readonly data = this.resolveDataAsSignal<{
		assignment?: AssignmentWithRelations;
		validationErrors: ValidationPhaseResponse;
	}>('assignmentData');
	private readonly reports = this.resolveDataAsSignal<PaginatedResponse<ReportDocument>>('reports');
	public readonly assignment = computed(() => this.data()?.assignment);
	public readonly validationData = computed(() => this.data()?.validationErrors);
	public readonly validationErrors = computed(() => {
		const errors = this.data()?.validationErrors;
		if (!errors) {
			return [];
		}
		const errs = Object.keys(errors)
			.map((key) => errors[key])
			.flat();

		return errs.filter((err) => err.severity.toLowerCase() === 'error');
	});
	public readonly clientId: Signal<number | string> = toSignal(
		this.activeRoute.data.pipe(map((data) => data['clientId'])),
	);
	private readonly assignmentId: Signal<number | string> = computed(() => this.assignment()!.id);
	public readonly targetPhase = computed(() => Number(this.assignment()?.phaseChangeId ?? DB_PHASES.FP4) as DB_PHASES);
	public readonly isReturn = computed(() => this.targetPhase() === DB_PHASES.FP5);
	public readonly titleKey = computed(() =>
		this.isReturn() ? 'assignment.fds.return_title' : 'assignment.fds.submit_title',
	);
	public readonly infoKey = computed(() =>
		this.isReturn() ? 'assignment.fds.return_info' : 'assignment.fds.submit_info',
	);
	public readonly actionKey = computed(() =>
		this.isReturn() ? 'assignment.fds.return_action' : 'assignment.fds.submit_action',
	);
	private readonly canTransitionPhases = hasPermission(this.session.permissions, 'Phases.Transition');
	public readonly saving = signal<boolean>(false);
	public readonly disableContinueButton = computed(() => {
		if (!this.canTransitionPhases()) return true;
		if (this.saving()) return true;
		if (this.validationErrors().length > 0) return true;
		return false;
	});
	public readonly fdsReport = computed(() =>
		(this.reports()?.entities ?? []).find((r) => r.categoryReport.id === REPORT_TYPE.FDS),
	);

	public async save() {
		if (this.saving()) return;

		this.saving.set(true);
		try {
			const target = this.targetPhase() || DB_PHASES.FP4;
			await this.api.moveServiceOfficeStatementPhase(this.clientId(), this.assignmentId(), target);
			await this.state.updateAssignments();
			const successKey = this.isReturn()
				? 'base.notification_message.assignment_returned'
				: 'base.notification_message.assignment_assigned';
			this.draftService.deleteDraft('phase', this.assignmentId()!.toString());
			await this.router.navigate(['../../'], { relativeTo: this.activeRoute });
			this.ntfy.addNotification(createSuccessNotification(this.transloco.translate(successKey)));
		} catch (error) {
			const errorMsg = this.transloco.translate('base.notification_message.assignment_assign_error');
			this.logger.error(error as Error);
			this.ntfy.addNotification(createErrorNotification(errorMsg));
		} finally {
			this.saving.set(false);
		}
	}

	public async cancel() {
		await this.api.startPhaseTransition(this.clientId(), this.assignmentId(), -1);
		await this.state.updateAssignments();
		this.draftService.deleteDraft('phase', this.assignmentId()!.toString());
		await this.router.navigate(['../../'], { relativeTo: this.activeRoute });
	}
}
